import { useEffect, useMemo, useState, Suspense, type ChangeEvent } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  alpha,
  AppBar,
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Collapse,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from '@/i18n';
import { EmergencyActiveAlertBanner } from '@/modules/emergency/components/EmergencyActiveAlertBanner';
import { EmergencyTriggerModal } from '@/modules/emergency/components/EmergencyTriggerModal';
import { AiAssistantFab } from '@/modules/ai/components/AiAssistantFab';
import { vyora } from '@/themes/tokens';
import { motion } from 'framer-motion';
import { APP_NAME } from '@/constants';
import { AppLogo } from '@/components/brand/AppLogo';
import { NotificationBell } from '@/components/common/NotificationBell';
import { ProfileMenu } from '@/components/common/ProfileMenu';
import { TenantSwitcher } from '@/components/common/TenantSwitcher';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { useUiStore } from '@/store/uiStore';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { useLogout } from '@/modules/auth/hooks/useAuth';
import { usePermission } from '@/permissions/usePermission';
import { resolveNavigation, getNavLabel, MENU_CONFIG, type MenuItemConfig } from '@/permissions/menuConfig';
import { useLicenseStatus } from '@/modules/license/hooks/useLicense';
import {
  usePropertyConfig,
  usePropertyStructure,
  usePropertyTerminology,
  useSocietyType,
} from '@/context/PropertyTypeContext';
import { useNavigationPreferences } from '@/hooks/useNavigationPreferences';

import { ImpersonationBanner } from '@/components/common/ImpersonationBanner';
import { MaintenanceBanner } from '@/components/common/MaintenanceBanner';
import { AppBodyLoader } from '@/components/common/AppLoader';
import { displayName, formatCurrency } from '@/utils/helpers';
import {
  MobileBottomNav,
  shouldShowMobileBottomNav,
  BOTTOM_NAV_HEIGHT,
} from '@/mobile/layouts/MobileShell';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COMPACT = 92;
const LS_FAVORITES = 'sm.nav.favorites';
const LS_PINNED = 'sm.nav.pinned';
const LS_COMPACT = 'sm.layout.compact';

function pathMatches(path: string, pathname: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Longest matching menu path wins, so /collections does not stay active on /collections/special-collections. */
function isPathActive(path: string | undefined, pathname: string, allPaths: string[] = []): boolean {
  if (!path) return false;
  if (!pathMatches(path, pathname)) return false;
  return !allPaths.some(
    (other) => other !== path && other.length > path.length && pathMatches(other, pathname),
  );
}

function flattenMenu(items: MenuItemConfig[]): MenuItemConfig[] {
  return items.flatMap((item) =>
    item.children?.length ? [item, ...flattenMenu(item.children)] : [item],
  );
}

function NavLink({
  item,
  active,
  compact,
  onNavigate,
}: {
  item: MenuItemConfig;
  active: boolean;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const { t, language, isRTL } = useTranslation();
  const Icon = item.icon;
  const label = getNavLabel(t, item, language);
  return (
    <Tooltip title={compact ? label : ''} placement={isRTL ? 'left' : 'right'}>
      <ListItemButton
        component={RouterLink}
        to={item.path || '#'}
        selected={active}
        onClick={() => onNavigate?.()}
        sx={{
          position: 'relative',
          borderRadius: 2,
          mb: 0.5,
          minHeight: 44,
          justifyContent: compact ? 'center' : 'flex-start',
          px: compact ? 1 : 1.75,
          py: 1,
          bgcolor: active ? 'primary.main' : 'transparent',
          color: active ? 'primary.contrastText' : 'inherit',
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
              color: 'primary.contrastText',
            },
          },
          '&:hover': {
            bgcolor: active ? 'primary.dark' : 'rgba(255,255,255,0.06)',
            color: active ? 'primary.contrastText' : '#fff',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: compact ? 0 : 36,
            mr: compact ? 0 : isRTL ? 0 : 1,
            ml: isRTL && !compact ? 1 : 0,
            color: active ? 'primary.contrastText' : 'primary.light',
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {!compact ? (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: active ? 700 : 500,
              fontSize: '0.875rem',
              color: 'inherit',
              textAlign: isRTL ? 'right' : 'left',
            }}
          />
        ) : null}
      </ListItemButton>
    </Tooltip>
  );
}

function NavSection({
  item,
  pathname,
  compact,
  openSectionId,
  setOpenSectionId,
  onNavigate,
  menuPaths,
}: {
  item: MenuItemConfig;
  pathname: string;
  compact?: boolean;
  openSectionId: string | null;
  setOpenSectionId: (id: string | null) => void;
  onNavigate?: () => void;
  menuPaths: string[];
}) {
  const { t, language, isRTL } = useTranslation();
  const Icon = item.icon;
  const label = getNavLabel(t, item, language);
  const children = item.children || [];
  const childActive = children.some((c) => isPathActive(c.path, pathname, menuPaths));
  const open = openSectionId === item.id;

  return (
    <Box sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={() => setOpenSectionId(open ? null : item.id)}
        sx={{
          borderRadius: 2,
          minHeight: 44,
          justifyContent: compact ? 'center' : 'flex-start',
          color: childActive ? 'primary.light' : 'inherit',
          px: compact ? 1 : 1.75,
          py: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: compact ? 0 : 36,
            color: childActive ? 'primary.light' : 'rgba(248,250,252,0.72)',
            mr: compact ? 0 : isRTL ? 0 : 1,
            ml: isRTL && !compact ? 1 : 0,
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {!compact ? (
          <>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontWeight: childActive ? 700 : 500,
                fontSize: '0.875rem',
                color: 'inherit',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </>
        ) : null}
      </ListItemButton>
      <Collapse in={open && !compact} timeout="auto" unmountOnExit>
        <List disablePadding sx={{ pl: isRTL ? 0 : 1.5, pr: isRTL ? 1.5 : 0 }}>
          {children.map((child) => (
            <NavLink
              key={child.id}
              item={child}
              active={isPathActive(child.path, pathname, menuPaths)}
              compact={compact}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

export function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const showBottomNav = shouldShowMobileBottomNav(isXs);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, setLanguage, languages, currentLanguage, isRTL } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const logoutMutation = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const [emergencyTriggerOpen, setEmergencyTriggerOpen] = useState(false);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [compactMode, setCompactMode] = useState(
    () => window.localStorage.getItem(LS_COMPACT) === '1',
  );
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(LS_FAVORITES) || '[]');
    } catch {
      return [];
    }
  });
  const [pinned, setPinned] = useState<string[]>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(LS_PINNED) || '[]');
    } catch {
      return [];
    }
  });
  const { can, hasAnyPermission, hasRole, hasFeature, isSuperAdmin: isSuperAdminFlag } = usePermission();
  const isPlatformAuth = useSuperAdminAuthStore((s) => s.isAuthenticated);
  const isSuperAdminMode = isSuperAdminFlag || Boolean(isPlatformAuth);
  const licenseQuery = useLicenseStatus();
  const propertyConfig = usePropertyConfig();
  const propertyStructure = usePropertyStructure();
  const propertyTerminology = usePropertyTerminology();
  const societyType = useSocietyType();
  const navPrefsQuery = useNavigationPreferences();

  const checkDateExpired = (validUntil?: string | Date | null) => {
    if (!validUntil) return false;
    const end = new Date(validUntil);
    end.setHours(23, 59, 59, 999);
    return Date.now() > end.getTime();
  };

  const isLicenseDataExpired = licenseQuery.data?.validUntil
    ? checkDateExpired(licenseQuery.data.validUntil)
    : user?.license?.validUntil
      ? checkDateExpired(user.license.validUntil)
      : false;

  const licenseLocked =
    !isSuperAdminMode &&
    (licenseQuery.data !== undefined
      ? licenseQuery.data?.locked === true || isLicenseDataExpired
      : user?.license?.locked === true || isLicenseDataExpired);

  const visibleNav = useMemo(
    () =>
      resolveNavigation(MENU_CONFIG, {
        isSuperAdmin: isSuperAdminMode,
        licenseLocked,
        societyType,
        propertyStructure,
        propertyTerminology,
        propertyPluralLabel: propertyConfig.pluralLabel,
        hasFeature,
        can,
        hasAnyPermission,
        hasRole,
        menuPreferences: navPrefsQuery.data,
      }),
    [
      isSuperAdminMode,
      licenseLocked,
      societyType,
      propertyStructure,
      propertyTerminology,
      propertyConfig.pluralLabel,
      hasFeature,
      can,
      hasAnyPermission,
      hasRole,
      navPrefsQuery.data,
    ],
  );

  const flatVisible = useMemo(() => flattenMenu(visibleNav).filter((i) => i.path), [visibleNav]);
  const menuPaths = useMemo(
    () => flattenMenu(MENU_CONFIG).map((item) => item.path).filter((p): p is string => Boolean(p)),
    [],
  );

  const menuById = useMemo(() => new Map(flatVisible.map((item) => [item.id, item])), [flatVisible]);
  const favoriteItems = favorites.map((id) => menuById.get(id)).filter(Boolean) as MenuItemConfig[];
  const pinnedItems = pinned.map((id) => menuById.get(id)).filter(Boolean) as MenuItemConfig[];

  const filteredNav = useMemo(() => {
    if (!search.trim()) return visibleNav;
    const needle = search.trim().toLowerCase();
    return visibleNav
      .map((item) => {
        const itemResolvedLabel = getNavLabel(t, item, language).toLowerCase();
        if (item.children?.length) {
          const children = item.children.filter((child) =>
            getNavLabel(t, child, language).toLowerCase().includes(needle),
          );
          if (itemResolvedLabel.includes(needle)) return item;
          return children.length ? { ...item, children } : null;
        }
        return itemResolvedLabel.includes(needle) ? item : null;
      })
      .filter(Boolean) as MenuItemConfig[];
  }, [visibleNav, search, t, language]);

  useEffect(() => {
    window.localStorage.setItem(LS_FAVORITES, JSON.stringify(favorites));
  }, [favorites]);
  useEffect(() => {
    window.localStorage.setItem(LS_PINNED, JSON.stringify(pinned));
  }, [pinned]);
  useEffect(() => {
    window.localStorage.setItem(LS_COMPACT, compactMode ? '1' : '0');
  }, [compactMode]);

  useEffect(() => {
    const activeSection = visibleNav.find((item) =>
      item.children?.some((c) => isPathActive(c.path, location.pathname, menuPaths)),
    );
    if (activeSection?.id) setOpenSectionId(activeSection.id);
  }, [location.pathname, visibleNav, menuPaths]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile, setSidebarOpen]);

  useEffect(() => {
    if (!licenseLocked) return;
    const path = location.pathname;
    const allowed =
      path.startsWith('/settings/license') ||
      path === '/profile' ||
      path === '/change-password' ||
      path === '/403';
    if (!allowed) {
      navigate('/settings/license', { replace: true });
    }
  }, [licenseLocked, location.pathname, navigate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleFavorite = (id: string) =>
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]));
  const togglePinned = (id: string) =>
    setPinned((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]));

  const closeMobileNav = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const goTo = (item: MenuItemConfig) => {
    if (!item.path) return;
    navigate(item.path);
    setCommandOpen(false);
    closeMobileNav();
  };

  const activeCrumb = flatVisible.find((item) => isPathActive(item.path, location.pathname, menuPaths));
  const parentSection = visibleNav.find(
    (sec) => sec.children?.some((child) => child.id === activeCrumb?.id || child.path === activeCrumb?.path),
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: compactMode ? 1 : 2.5, py: 2.5, display: 'flex', justifyContent: compactMode ? 'center' : 'flex-start' }}>
        {compactMode ? (
          <AppLogo variant="mark" />
        ) : (
          <AppLogo onDark />
        )}
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {!compactMode && favoriteItems.length ? (
          <>
            <Typography sx={{ px: 1.5, py: 0.5, color: 'rgba(248,250,252,0.45)' }} variant="caption">
              {t('common.favorites')}
            </Typography>
            {favoriteItems.map((item) => (
              <NavLink
                key={`fav-${item.id}`}
                item={item}
                active={isPathActive(item.path, location.pathname, menuPaths)}
                compact={compactMode}
                onNavigate={closeMobileNav}
              />
            ))}
          </>
        ) : null}
        {!compactMode && pinnedItems.length ? (
          <>
            <Typography sx={{ px: 1.5, py: 0.5, color: 'rgba(248,250,252,0.45)' }} variant="caption">
              {t('common.pinned')}
            </Typography>
            {pinnedItems.map((item) => (
              <NavLink
                key={`pin-${item.id}`}
                item={item}
                active={isPathActive(item.path, location.pathname, menuPaths)}
                compact={compactMode}
                onNavigate={closeMobileNav}
              />
            ))}
          </>
        ) : null}
        {filteredNav.map((item) =>
          item.children?.length ? (
            <NavSection
              key={item.id}
              item={item}
              pathname={location.pathname}
              compact={compactMode}
              openSectionId={openSectionId}
              setOpenSectionId={setOpenSectionId}
              onNavigate={closeMobileNav}
              menuPaths={menuPaths}
            />
          ) : (
            <NavLink
              key={item.id}
              item={item}
              active={isPathActive(item.path, location.pathname, menuPaths)}
              compact={compactMode}
              onNavigate={closeMobileNav}
            />
          ),
        )}
      </List>
      <Box sx={{ p: compactMode ? 1 : 2 }}>
        <Box
          sx={{
            p: compactMode ? 1 : 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.06)',
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.08)',
          }}
        >
          {!compactMode ? (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.5)' }} display="block">
                {t('common.tenant')}
              </Typography>
              <Typography variant="body2" fontWeight={650} noWrap>
                {tenant?.name || (isSuperAdminMode ? 'ApnaBlock' : 'No tenant selected')}
              </Typography>
            </>
          ) : (
            <Tooltip title={tenant?.name || t('common.tenant')}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t('common.tenant')}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <AppBar
        className="no-print"
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH}px)` },
          ml: isRTL ? undefined : { md: `${compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH}px` },
          mr: isRTL ? { md: `${compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH}px` } : undefined,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: (tTheme) => alpha(tTheme.palette.background.paper, 0.86),
          pt: 'var(--safe-area-top, env(safe-area-inset-top, 0px))',
        }}
      >
        <Toolbar sx={{ gap: 0.5, flexWrap: { xs: 'nowrap', sm: 'nowrap' }, minWidth: 0 }}>
          {isMobile ? (
            <IconButton edge="start" onClick={() => setSidebarOpen(true)} aria-label={t('common.openMenu')}>
              <MenuIcon />
            </IconButton>
          ) : null}
          <Tooltip title={compactMode ? t('common.expandMenu') : t('common.minimizeMenu')}>
            <IconButton
              onClick={() => setCompactMode((v) => !v)}
              aria-label={compactMode ? t('common.expandMenu') : t('common.minimizeMenu')}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                borderRadius: '8px',
                transition: 'background-color 180ms ease, color 180ms ease, transform 180ms ease',
                '&:hover': {
                  bgcolor: (tTheme) => alpha(tTheme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  transform: 'translateX(-1px)',
                },
              }}
            >
              {compactMode ? <ChevronRightOutlinedIcon /> : <ChevronLeftOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1, overflow: 'hidden' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 650,
                fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' },
                lineHeight: 1.2,
              }}
              noWrap
            >
              {tenant?.name || (isSuperAdminMode ? 'ApnaBlock' : APP_NAME)}
            </Typography>
            <Breadcrumbs
              separator="›"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                mt: 0.25,
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                },
                '& .MuiBreadcrumbs-li': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiBreadcrumbs-separator': {
                  mx: 0.5,
                  flexShrink: 0,
                },
              }}
            >
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to={isSuperAdminMode ? '/super-admin' : '/dashboard'}
                variant="caption"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {t('common.home')}
              </Link>
              {parentSection ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {getNavLabel(t, parentSection, language)}
                </Typography>
              ) : null}
              {activeCrumb && (!parentSection || parentSection.id !== activeCrumb.id) ? (
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: 150, sm: 260, md: 360 },
                    display: 'inline-block',
                    verticalAlign: 'bottom',
                  }}
                >
                  {getNavLabel(t, activeCrumb, language)}
                </Typography>
              ) : null}
            </Breadcrumbs>
          </Box>

          <TenantSwitcher />

          {!isSuperAdminMode && (
            <Tooltip title={t('emergency.triggerAlert')}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => setEmergencyTriggerOpen(true)}
                aria-label={t('emergency.triggerAlert')}
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  bgcolor: '#EF4444',
                  minWidth: { xs: 36, md: 40, lg: 64 },
                  width: { xs: 36, md: 40, lg: 'auto' },
                  height: 36,
                  p: { xs: 0, lg: '6px 14px' },
                  borderRadius: { xs: '50%', lg: 2 },
                  boxShadow: '0 0 10px rgba(239, 68, 68, 0.45)',
                  '&:hover': { bgcolor: '#DC2626' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <LocalFireDepartmentIcon sx={{ fontSize: { xs: 20, lg: 18 } }} />
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', lg: 'inline' },
                    ml: 0.5,
                    letterSpacing: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('emergency.emergencyAlert')}
                </Box>
              </Button>
            </Tooltip>
          )}

          <NotificationBell />

          <Tooltip title={t('common.selectLanguage')}>
            <IconButton
              onClick={(e) => setLanguageAnchorEl(e.currentTarget)}
              aria-label={t('common.selectLanguage')}
              sx={{
                borderRadius: '8px',
                px: 1,
                gap: 0.5,
                color: 'inherit',
              }}
            >
              <LanguageIcon fontSize="small" />
              <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                {currentLanguage.badge || language.toUpperCase()}
              </Box>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={() => setLanguageAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={language === lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setLanguageAnchorEl(null);
                }}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 160 }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={language === lang.code ? 700 : 500}>
                    {lang.nativeName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lang.name}
                  </Typography>
                </Box>
                {language === lang.code ? <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} /> : null}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
            <IconButton onClick={toggleTheme} aria-label={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label={t('common.accountMenu')}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
              {displayName(user).slice(0, 1).toUpperCase()}
            </Avatar>
          </IconButton>
          <ProfileMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            user={user}
            onProfileClick={() => {
              setAnchorEl(null);
              navigate(isSuperAdmin() ? '/super-admin/profile' : '/profile');
            }}
            onChangePasswordClick={() => {
              setAnchorEl(null);
              navigate(isSuperAdmin() ? '/super-admin/change-password' : '/change-password');
            }}
            onLogoutClick={() => {
              setAnchorEl(null);
              logoutMutation.mutate(undefined, {
                onSettled: () => navigate('/login', { replace: true }),
              });
            }}
          />
        </Toolbar>
      </AppBar>

      <Box
        className="no-print"
        component="nav"
        sx={{ width: { md: compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          anchor={isRTL ? 'right' : 'left'}
          open={isMobile && sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH,
              bgcolor: vyora.navy,
              color: 'rgba(248,250,252,0.88)',
              borderRight: isRTL ? 'none' : '1px solid rgba(255,255,255,0.08)',
              borderLeft: isRTL ? '1px solid rgba(255,255,255,0.08)' : 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL ? 'right' : 'left'}
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH,
              boxSizing: 'border-box',
              bgcolor: vyora.navy,
              color: 'rgba(248,250,252,0.88)',
              borderRight: isRTL ? 'none' : '1px solid rgba(255,255,255,0.08)',
              borderLeft: isRTL ? '1px solid rgba(255,255,255,0.08)' : 'none',
              transition: 'width 220ms cubic-bezier(0.22, 1, 0.36, 1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          width: { md: `calc(100% - ${compactMode ? DRAWER_WIDTH_COMPACT : DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          pt: 'calc(72px + var(--safe-area-top, env(safe-area-inset-top, 0px)))',
          px: { xs: 1.5, sm: 2.5, md: 3 },
          pb: showBottomNav && !licenseLocked
            ? `calc(${BOTTOM_NAV_HEIGHT + 24}px + var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))`
            : 4,
        }}
      >
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
        >
          {!isSuperAdminMode && <EmergencyActiveAlertBanner />}
          <MaintenanceBanner />
          <ImpersonationBanner />
          <Box className="app-page" sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            {!licenseLocked &&
              licenseQuery.data?.pendingExpansion &&
              !location.pathname.startsWith('/settings/license') ? (
              <Alert
                severity="warning"
                sx={{ mb: 2, cursor: 'pointer' }}
                onClick={() => navigate('/settings/license')}
              >
                Invoice for additional {licenseQuery.data.pendingExpansion.additionalHouses} house
                {licenseQuery.data.pendingExpansion.additionalHouses === 1 ? '' : 's'} ·{' '}
                {formatCurrency(licenseQuery.data.pendingExpansion.amountInr)} for{' '}
                {licenseQuery.data.pendingExpansion.remainingDays} remaining day
                {licenseQuery.data.pendingExpansion.remainingDays === 1 ? '' : 's'}. Pay on
                Subscription to add more houses.
              </Alert>
            ) : null}
            <Suspense fallback={<AppBodyLoader />}>
              <Outlet />
            </Suspense>
          </Box>
        </motion.div>
      </Box>
      <Box className="no-print">
        {licenseLocked ? null : <MobileBottomNav />}
        {licenseLocked ? null : <AiAssistantFab />}
      </Box>
      {!isSuperAdminMode && (
        <EmergencyTriggerModal
          open={emergencyTriggerOpen}
          onClose={() => setEmergencyTriggerOpen(false)}
        />
      )}
      <Dialog open={commandOpen} onClose={() => setCommandOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.commandPalette')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('common.searchPages')}
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack spacing={0.5}>
            {flatVisible
              .filter((item) =>
                getNavLabel(t, item, language).toLowerCase().includes((search || '').trim().toLowerCase()),
              )
              .slice(0, 12)
              .map((item) => (
                <ListItemButton
                  key={`cmd-${item.id}`}
                  onClick={() => goTo(item)}
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.8),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <item.icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={getNavLabel(t, item, language)} />
                  <Tooltip title={t('common.favorite')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}>
                      <StarBorderOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.pin')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); togglePinned(item.id); }}>
                      <PushPinOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
              ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
